/**
 * EmbeddedInvoice — the actual invoice document, rendered read-only inside
 * the inbox reading pane so the founder reviews the real thing (letterhead,
 * line items, GST totals) without leaving the inbox.
 *
 * Fetches `invoices.get` for the approval's entity and renders a compact
 * version of the printable document. Money/qty come straight off the wire
 * row (stringified paise, qty in milli) and format via `@/lib/format`.
 */
import { trpc } from "../../lib/trpc.ts";
import { paiseToInrExact, formatDate, deriveInitials } from "../../lib/format.ts";

export function EmbeddedInvoice({ invoiceId, sellerName }: { invoiceId: string; sellerName: string }) {
  const q = trpc.invoices.get.useQuery({ invoiceId });

  if (q.isLoading) {
    return <div className="rounded border border-line bg-paper p-6 text-sm text-muted">Loading invoice…</div>;
  }
  if (q.error || !q.data) {
    return (
      <div className="rounded border border-line bg-paper p-6 text-sm text-muted">
        Couldn't load the invoice document. Use “Open record” to view it.
      </div>
    );
  }

  const { invoice, lines, customer } = q.data;
  const addr = [customer.addressLine1, customer.addressLine2, customer.city, customer.state]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="overflow-hidden rounded border border-line bg-paper shadow-card">
      {/* Letterhead */}
      <div className="flex items-start justify-between gap-4 border-b-2 border-ink px-6 py-5">
        <div className="flex gap-3">
          <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-ink text-sm font-extrabold text-white">
            {deriveInitials(sellerName)}
          </div>
          <div>
            <h2 className="text-base font-bold text-ink">{sellerName}</h2>
            <p className="mt-0.5 text-xs leading-snug text-muted">Tax invoice</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-bold uppercase tracking-widest text-muted">Tax Invoice</div>
          <div className="mt-0.5 font-mono text-lg font-bold tracking-tight text-ink">
            {invoice.docNumber || "Draft"}
          </div>
          <div className="font-mono text-xs text-ink-2">Issued {formatDate(invoice.issuedOn)}</div>
        </div>
      </div>

      {/* Bill-to + dates */}
      <div className="grid grid-cols-[1.3fr_1fr] gap-4 border-b border-line px-6 py-4">
        <div>
          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted">Bill to</div>
          <div className="text-sm font-bold text-ink">{customer.name}</div>
          {addr ? <div className="mt-0.5 text-xs leading-snug text-muted">{addr}</div> : null}
          {customer.gstin ? (
            <div className="mt-0.5 font-mono text-xs text-muted">GSTIN {customer.gstin}</div>
          ) : null}
        </div>
        <div className="grid grid-cols-2 content-start gap-x-2.5 gap-y-3">
          <Cell k="Invoice date" v={formatDate(invoice.issuedOn)} />
          <Cell k="Due date" v={formatDate(invoice.dueOn)} />
          <Cell k="Place of supply" v={invoice.placeOfSupply} />
          <Cell k="Status" v={invoice.status} />
        </div>
      </div>

      {/* Line items */}
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <Th>Item</Th>
            <Th right>Qty</Th>
            <Th right>Rate</Th>
            <Th right>Amount</Th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id}>
              <td className="border-b border-line px-6 py-2.5 align-top text-sm">
                <div className="font-semibold text-ink">{l.description}</div>
                <div className="mt-0.5 text-xs text-muted">
                  HSN {l.hsn} · {(l.taxRateBps / 100).toString()}% GST
                </div>
              </td>
              <td className="border-b border-line px-6 py-2.5 text-right align-top font-mono text-sm">
                {(l.qtyMilli / 1000).toLocaleString("en-IN")} {l.unit}
              </td>
              <td className="border-b border-line px-6 py-2.5 text-right align-top font-mono text-sm">
                {paiseToInrExact(l.ratePaise)}
              </td>
              <td className="border-b border-line px-6 py-2.5 text-right align-top font-mono text-sm">
                {paiseToInrExact(l.lineTotalPaise)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end px-6 py-4">
        <div className="w-72">
          <Row k="Taxable value" v={paiseToInrExact(invoice.subtotalPaise)} />
          {invoice.cgstPaise !== "0" ? <Row k="CGST" v={paiseToInrExact(invoice.cgstPaise)} /> : null}
          {invoice.sgstPaise !== "0" ? <Row k="SGST" v={paiseToInrExact(invoice.sgstPaise)} /> : null}
          {invoice.igstPaise !== "0" ? <Row k="IGST" v={paiseToInrExact(invoice.igstPaise)} /> : null}
          <div className="mt-1.5 flex justify-between border-t-2 border-ink pt-2.5 text-base font-bold text-ink">
            <span>Total</span>
            <span className="font-mono">{paiseToInrExact(invoice.totalPaise)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Cell({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wide text-muted">{k}</div>
      <div className="mt-0.5 text-sm font-semibold text-ink">{v}</div>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`border-b border-line bg-raised px-6 py-2.5 text-xs font-bold uppercase tracking-wide text-muted ${
        right ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between py-1 text-sm text-ink-2">
      <span>{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}
